import classNames from 'classnames';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import CleanIcon from 'src/assets/clean-icon.svg';
import FuelIcon from 'src/assets/fuel-icon.svg';
import NoResultsImg from 'src/assets/no-results.svg';
import ServiceIcon from 'src/assets/service-icon.svg';
import {
  getErrorsTable,
  setErrorsTable,
} from 'src/store/actions/productActions';
import { DeviceError } from 'src/store/models/Device';
import { colors } from 'src/utils/colors';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import { Error, ErrorIcon } from '../../../store/models/Errors';
import { useQueryParams } from '../../../utils/useQueryParams';
import EmptyState from '../../ui/EmptyState/EmptyState';
import PageHeader from '../../ui/PageHeader/PageHeader';
import Table from '../../ui/Table/Table';
import {
  TableHeader,
  TableQueryParameters,
} from '../../ui/Table/Table.interface';
import TableSearch from '../../ui/Table/TableSearch/TableSearch';
import classes from './ErrorIndex.module.scss';

const ErrorIndex: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const locationState = location?.state as DeviceError;

  const { urlParams } = useQueryParams<TableQueryParameters>(location.search);

  const errors = useAppSelector((state) => state.product.errors);
  const loading = useLoadingSelector({ actionType: getErrorsTable.toString() });

  const getIcon = (icon: ErrorIcon) => {
    switch (icon) {
      case ErrorIcon.CLEAN:
        return CleanIcon;
      case ErrorIcon.SERVICE:
        return ServiceIcon;
      case ErrorIcon.FUEL:
        return FuelIcon;
      default:
        return null;
    }
  };

  const headers: TableHeader<Error>[] = [
    {
      accessor: 'id',
      label: t('error_index:error_code'),
      fontWeight: 700,
    },
    {
      accessor: 'logCode',
      label: t('error_index:error_log_code'),
    },
    {
      accessor: 'alphaUiSymbol',
      label: t('error_index:alpha_ui_symbols'),
      component: (e) => (
        <>
          <div className={classes.AlphaSymbols}>
            {e?.icons?.map((icon, i) => (
              <img src={getIcon(icon)} key={i} alt={icon} />
            ))}
          </div>
          <p>{e?.alphaUiSymbol}</p>
        </>
      ),
    },
    {
      accessor: 'description',
      label: t('error_index:error_description'),
    },
    {
      accessor: 'alpha_60_65_70_75',
      label: t('error_index:alpha_controller'),
      component: (e) => {
        if (
          e.alpha_40 === e.alpha_41_42 &&
          e.alpha_40 === e.alpha_60_65_70_75
        ) {
          return e.alpha_60_65_70_75;
        } else if (e.alpha_40 === e.alpha_41_42) {
          return `40/41/42: ${e.alpha_41_42}\n60/65/70/75: ${e.alpha_60_65_70_75}`;
        }
        return `40: ${e.alpha_40}\n41/42: ${e.alpha_41_42}\n60/65/70/75: ${e.alpha_60_65_70_75}`;
      },
    },
    {
      accessor: 'notes',
      label: t('common:notes'),
    },
  ];

  useEffect(() => {
    dispatch(setErrorsTable([]));
    return () => {
      dispatch(setErrorsTable([]));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(getErrorsTable({ params: urlParams }));
    // eslint-disable-next-line
  }, [location]);

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('error_index:error_index')}
        showBackButton
        className={classes.PageHeader}
        endNode={<TableSearch urlParams={urlParams} />}
      />
      <Table
        className={classNames(classes.Table, { [classes.Loading]: loading })}
        headers={headers}
        data={errors}
        highlightedId={locationState?.logCode?.toString()}
        highlightColor={colors()?.colorGray2}
        loading={loading}
        emptyTableComponent={
          <EmptyState
            title={t('common:no_results')}
            subtitle={t('common:no_results_subtitle')}
            icon={NoResultsImg}
          />
        }
      />
    </div>
  );
};

export default ErrorIndex;
